import React, { useState, useRef } from 'react';
import { X, Upload, Mic, Image as ImageIcon, Info, Phone, ExternalLink, Square, Play } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getMediaUrl } from '../utils/mediaUrl';

// Compact local translations map so EditProfileModal is fully localised
// without bloating the global translations.js file.
const EP_L = {
  fr: { title: 'Modifier le profil', photo: 'Photo de profil', choosePhoto: 'Choisir une photo', photoHint: 'JPG, PNG ou WEBP. Max 5 MB.', username: "Nom d'utilisateur", usernameRule: '3 à 20 caractères : lettres minuscules, chiffres ou underscore.', available: '✓ Disponible', bio: 'Biographie', bioPh: 'Parlez-nous de vous...', audioIntro: 'Audio de présentation', upload: 'Téléverser', replace: 'Remplacer', record: 'Enregistrer (60s)', currentAudio: 'Audio actuel', newRecording: 'Nouvel enregistrement', noAudio: 'Aucun audio', audioHint: "MP3, WAV, M4A, OGG. Max 10 MB ou 60s d'enregistrement.", phone: 'Numéro de téléphone', phoneHint: 'Visible dans les discussions si activé dans Réglages > Confidentialité', externalLink: 'Lien externe', externalLinkHint: "S'affichera sur votre profil public", cancel: 'Annuler', save: 'Enregistrer', saving: 'Enregistrement…' },
  en: { title: 'Edit profile', photo: 'Profile picture', choosePhoto: 'Choose a photo', photoHint: 'JPG, PNG or WEBP. Max 5 MB.', username: 'Username', usernameRule: '3–20 chars: lowercase letters, digits or underscore.', available: '✓ Available', bio: 'Bio', bioPh: 'Tell us about yourself…', audioIntro: 'Intro audio', upload: 'Upload', replace: 'Replace', record: 'Record (60s)', currentAudio: 'Current audio', newRecording: 'New recording', noAudio: 'No audio', audioHint: 'MP3, WAV, M4A, OGG. Max 10 MB or 60s recording.', phone: 'Phone number', phoneHint: 'Visible in chats if enabled in Settings > Privacy', externalLink: 'External link', externalLinkHint: 'Will show on your public profile', cancel: 'Cancel', save: 'Save', saving: 'Saving…' },
  es: { title: 'Editar perfil', photo: 'Foto de perfil', choosePhoto: 'Elegir una foto', photoHint: 'JPG, PNG o WEBP. Máx 5 MB.', username: 'Nombre de usuario', usernameRule: '3-20 caracteres: minúsculas, dígitos o guion bajo.', available: '✓ Disponible', bio: 'Biografía', bioPh: 'Cuéntanos sobre ti…', audioIntro: 'Audio de presentación', upload: 'Subir', replace: 'Reemplazar', record: 'Grabar (60s)', currentAudio: 'Audio actual', newRecording: 'Nueva grabación', noAudio: 'Sin audio', audioHint: 'MP3, WAV, M4A, OGG. Máx 10 MB o 60 s de grabación.', phone: 'Número de teléfono', phoneHint: 'Visible en chats si está activo en Ajustes > Privacidad', externalLink: 'Enlace externo', externalLinkHint: 'Se mostrará en tu perfil público', cancel: 'Cancelar', save: 'Guardar', saving: 'Guardando…' },
  pt: { title: 'Editar perfil', photo: 'Foto de perfil', choosePhoto: 'Escolher uma foto', photoHint: 'JPG, PNG ou WEBP. Máx 5 MB.', username: 'Nome de utilizador', usernameRule: '3–20 caracteres: minúsculas, dígitos ou underscore.', available: '✓ Disponível', bio: 'Biografia', bioPh: 'Fale de si…', audioIntro: 'Áudio de apresentação', upload: 'Enviar', replace: 'Substituir', record: 'Gravar (60s)', currentAudio: 'Áudio atual', newRecording: 'Nova gravação', noAudio: 'Sem áudio', audioHint: 'MP3, WAV, M4A, OGG. Máx 10 MB ou 60 s de gravação.', phone: 'Número de telefone', phoneHint: 'Visível nos chats se ativado em Definições > Privacidade', externalLink: 'Link externo', externalLinkHint: 'Aparecerá no seu perfil público', cancel: 'Cancelar', save: 'Guardar', saving: 'A guardar…' },
  de: { title: 'Profil bearbeiten', photo: 'Profilbild', choosePhoto: 'Foto auswählen', photoHint: 'JPG, PNG oder WEBP. Max 5 MB.', username: 'Benutzername', usernameRule: '3–20 Zeichen: Kleinbuchstaben, Ziffern oder Unterstrich.', available: '✓ Verfügbar', bio: 'Biografie', bioPh: 'Erzähl uns von dir…', audioIntro: 'Vorstellungs-Audio', upload: 'Hochladen', replace: 'Ersetzen', record: 'Aufnehmen (60s)', currentAudio: 'Aktuelles Audio', newRecording: 'Neue Aufnahme', noAudio: 'Kein Audio', audioHint: 'MP3, WAV, M4A, OGG. Max 10 MB oder 60 s Aufnahme.', phone: 'Telefonnummer', phoneHint: 'In Chats sichtbar, wenn in Einstellungen > Datenschutz aktiviert', externalLink: 'Externer Link', externalLinkHint: 'Wird auf deinem öffentlichen Profil angezeigt', cancel: 'Abbrechen', save: 'Speichern', saving: 'Speichern…' },
  it: { title: 'Modifica profilo', photo: 'Foto profilo', choosePhoto: 'Scegli una foto', photoHint: 'JPG, PNG o WEBP. Max 5 MB.', username: 'Nome utente', usernameRule: '3–20 caratteri: minuscole, cifre o trattino basso.', available: '✓ Disponibile', bio: 'Biografia', bioPh: 'Raccontaci di te…', audioIntro: 'Audio di presentazione', upload: 'Carica', replace: 'Sostituisci', record: 'Registra (60s)', currentAudio: 'Audio attuale', newRecording: 'Nuova registrazione', noAudio: 'Nessun audio', audioHint: 'MP3, WAV, M4A, OGG. Max 10 MB o 60 s di registrazione.', phone: 'Numero di telefono', phoneHint: 'Visibile nelle chat se abilitato in Impostazioni > Privacy', externalLink: 'Link esterno', externalLinkHint: 'Verrà mostrato sul tuo profilo pubblico', cancel: 'Annulla', save: 'Salva', saving: 'Salvataggio…' },
  ru: { title: 'Редактировать профиль', photo: 'Фото профиля', choosePhoto: 'Выбрать фото', photoHint: 'JPG, PNG или WEBP. Макс 5 МБ.', username: 'Имя пользователя', usernameRule: '3–20 симв.: строчные, цифры или подчёркивание.', available: '✓ Доступно', bio: 'Биография', bioPh: 'Расскажите о себе…', audioIntro: 'Аудио-презентация', upload: 'Загрузить', replace: 'Заменить', record: 'Запись (60с)', currentAudio: 'Текущее аудио', newRecording: 'Новая запись', noAudio: 'Нет аудио', audioHint: 'MP3, WAV, M4A, OGG. Макс 10 МБ или 60 с.', phone: 'Телефон', phoneHint: 'Видно в чатах, если включено в Настройках > Приватность', externalLink: 'Внешняя ссылка', externalLinkHint: 'Будет показана в публичном профиле', cancel: 'Отмена', save: 'Сохранить', saving: 'Сохранение…' },
  ar: { title: 'تعديل الملف الشخصي', photo: 'صورة الملف', choosePhoto: 'اختر صورة', photoHint: 'JPG أو PNG أو WEBP. الحدّ الأقصى 5 ميغابايت.', username: 'اسم المستخدم', usernameRule: '3–20 حرفًا: أحرف صغيرة أو أرقام أو شرطة سفلية.', available: '✓ متاح', bio: 'نبذة', bioPh: 'أخبرنا عن نفسك…', audioIntro: 'صوت التقديم', upload: 'رفع', replace: 'استبدال', record: 'تسجيل (60 ث)', currentAudio: 'الصوت الحالي', newRecording: 'تسجيل جديد', noAudio: 'لا يوجد صوت', audioHint: 'MP3, WAV, M4A, OGG. الحدّ 10 ميغابايت أو 60 ث.', phone: 'رقم الهاتف', phoneHint: 'يظهر في المحادثات إذا فُعِّل في الإعدادات > الخصوصية', externalLink: 'رابط خارجي', externalLinkHint: 'سيظهر في ملفك العام', cancel: 'إلغاء', save: 'حفظ', saving: 'جارٍ الحفظ…' },
  zh: { title: '编辑资料', photo: '头像', choosePhoto: '选择照片', photoHint: 'JPG、PNG 或 WEBP。最大 5 MB。', username: '用户名', usernameRule: '3–20 个字符：小写字母、数字或下划线。', available: '✓ 可用', bio: '简介', bioPh: '介绍一下你自己…', audioIntro: '自我介绍音频', upload: '上传', replace: '替换', record: '录制 (60秒)', currentAudio: '当前音频', newRecording: '新录音', noAudio: '暂无音频', audioHint: 'MP3、WAV、M4A、OGG。最大 10 MB 或 60 秒。', phone: '电话号码', phoneHint: '若在 设置 > 隐私 中启用，则在聊天中可见', externalLink: '外部链接', externalLinkHint: '将显示在你的公开资料上', cancel: '取消', save: '保存', saving: '保存中…' },
  ja: { title: 'プロフィール編集', photo: 'プロフィール写真', choosePhoto: '写真を選択', photoHint: 'JPG、PNG または WEBP。最大 5 MB。', username: 'ユーザー名', usernameRule: '3〜20 文字：小文字・数字・アンダースコア。', available: '✓ 利用可能', bio: '自己紹介', bioPh: '自分について書いてください…', audioIntro: '紹介音声', upload: 'アップロード', replace: '差し替え', record: '録音 (60秒)', currentAudio: '現在の音声', newRecording: '新規録音', noAudio: '音声なし', audioHint: 'MP3、WAV、M4A、OGG。最大 10 MB または 60 秒。', phone: '電話番号', phoneHint: '設定 > プライバシー で有効化するとチャットに表示', externalLink: '外部リンク', externalLinkHint: '公開プロフィールに表示されます', cancel: 'キャンセル', save: '保存', saving: '保存中…' },
  hi: { title: 'प्रोफ़ाइल संपादित करें', photo: 'प्रोफ़ाइल फ़ोटो', choosePhoto: 'फ़ोटो चुनें', photoHint: 'JPG, PNG या WEBP. अधिकतम 5 MB।', username: 'यूज़रनेम', usernameRule: '3–20 अक्षर: छोटे अक्षर, अंक या अंडरस्कोर।', available: '✓ उपलब्ध', bio: 'बायो', bioPh: 'अपने बारे में बताएं…', audioIntro: 'परिचय ऑडियो', upload: 'अपलोड', replace: 'बदलें', record: 'रिकॉर्ड करें (60से.)', currentAudio: 'वर्तमान ऑडियो', newRecording: 'नई रिकॉर्डिंग', noAudio: 'कोई ऑडियो नहीं', audioHint: 'MP3, WAV, M4A, OGG. अधिकतम 10 MB या 60 से.', phone: 'फ़ोन नंबर', phoneHint: 'सेटिंग्स > गोपनीयता में सक्षम होने पर चैट में दिखता है', externalLink: 'बाहरी लिंक', externalLinkHint: 'आपकी सार्वजनिक प्रोफ़ाइल पर दिखेगा', cancel: 'रद्द करें', save: 'सहेजें', saving: 'सहेज रहा है…' },
  nl: { title: 'Profiel bewerken', photo: 'Profielfoto', choosePhoto: 'Kies een foto', photoHint: 'JPG, PNG of WEBP. Max 5 MB.', username: 'Gebruikersnaam', usernameRule: '3–20 tekens: kleine letters, cijfers of underscore.', available: '✓ Beschikbaar', bio: 'Bio', bioPh: 'Vertel iets over jezelf…', audioIntro: 'Intro-audio', upload: 'Uploaden', replace: 'Vervangen', record: 'Opnemen (60s)', currentAudio: 'Huidige audio', newRecording: 'Nieuwe opname', noAudio: 'Geen audio', audioHint: 'MP3, WAV, M4A, OGG. Max 10 MB of 60 s opname.', phone: 'Telefoonnummer', phoneHint: 'Zichtbaar in chats indien ingeschakeld in Instellingen > Privacy', externalLink: 'Externe link', externalLinkHint: 'Wordt op je openbare profiel getoond', cancel: 'Annuleren', save: 'Opslaan', saving: 'Opslaan…' },
  sw: { title: 'Hariri wasifu', photo: 'Picha ya wasifu', choosePhoto: 'Chagua picha', photoHint: 'JPG, PNG au WEBP. Kiwango cha juu 5 MB.', username: 'Jina la mtumiaji', usernameRule: 'Herufi 3–20: herufi ndogo, tarakimu au underscore.', available: '✓ Inapatikana', bio: 'Wasifu', bioPh: 'Tuambie kuhusu wewe…', audioIntro: 'Sauti ya utangulizi', upload: 'Pakia', replace: 'Badilisha', record: 'Rekodi (60s)', currentAudio: 'Sauti ya sasa', newRecording: 'Kurekodi mpya', noAudio: 'Hakuna sauti', audioHint: 'MP3, WAV, M4A, OGG. Kiwango cha juu 10 MB au sekunde 60.', phone: 'Nambari ya simu', phoneHint: 'Inaonekana kwenye mazungumzo ikiwa imewezeshwa katika Mipangilio > Faragha', externalLink: 'Kiungo cha nje', externalLinkHint: 'Itaonekana kwenye wasifu wako wa umma', cancel: 'Ghairi', save: 'Hifadhi', saving: 'Inahifadhi…' },
  ht: { title: 'Modifye pwofil', photo: 'Foto pwofil', choosePhoto: 'Chwazi yon foto', photoHint: 'JPG, PNG oswa WEBP. Maksimòm 5 MB.', username: 'Non itilizatè', usernameRule: '3–20 karaktè : lèt minuscul, chif oswa underscore.', available: '✓ Disponib', bio: 'Byografi', bioPh: 'Pale nou de tèt ou…', audioIntro: 'Odyo prezantasyon', upload: 'Voye', replace: 'Ranplase', record: 'Anrejistre (60s)', currentAudio: 'Odyo aktyèl', newRecording: 'Nouvo anrejistreman', noAudio: 'Pa gen odyo', audioHint: 'MP3, WAV, M4A, OGG. Maksimòm 10 MB oswa 60s.', phone: 'Nimewo telefòn', phoneHint: 'Vizib nan diskisyon si aktive nan Paramèt > Konfidansyalite', externalLink: 'Lyen ekstèn', externalLinkHint: 'Ap parèt sou pwofil piblik ou', cancel: 'Anile', save: 'Anrejistre', saving: 'K ap anrejistre…' },
};

const EditProfileModal = ({ onClose, onSuccess, currentUser }) => {
  const keyboardHeight = useKeyboardHeight();
  const { refreshUser } = useAuth();
  const { language } = useLanguage();
  const L = EP_L[language] || EP_L.fr;
  const [uploading, setUploading] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [bioAudio, setBioAudio] = useState(null);
  const [bioAudioPreviewUrl, setBioAudioPreviewUrl] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingStartRef = useRef(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [bio, setBio] = useState(currentUser?.bio || '');
  const [age, setAge] = useState(currentUser?.age || '');
  const [dateOfBirth, setDateOfBirth] = useState(currentUser?.date_of_birth || '');
  const [siret, setSiret] = useState(currentUser?.siret || '');
  const [websiteUrl, setWebsiteUrl] = useState(currentUser?.website_url || '');
  const [phoneNumber, setPhoneNumber] = useState(currentUser?.phone_number || '');
  // Username editing state — checked against the backend with a small debounce
  // so we can show "déjà pris" before the user submits.
  const [username, setUsername] = useState(currentUser?.username || '');
  const [usernameStatus, setUsernameStatus] = useState({ available: null, reason: '' });
  const usernameCheckTimer = useRef(null);

  const isPro = currentUser?.account_type === 'PRO';

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image trop volumineuse (max 5 MB)');
      return;
    }

    setProfilePicture(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setProfilePreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleBioAudioChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Audio trop volumineux (max 10 MB)');
      return;
    }

    setBioAudio(file);
    try {
      if (bioAudioPreviewUrl) URL.revokeObjectURL(bioAudioPreviewUrl);
      setBioAudioPreviewUrl(URL.createObjectURL(file));
    } catch (_) {}
  };

  const startRecordingBio = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType =
        typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : (MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm');
      const mr = new MediaRecorder(stream, { mimeType });
      recordedChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) recordedChunksRef.current.push(e.data); };
      mr.onstop = () => {
        const ext = mimeType.includes('mp4') ? 'm4a' : 'webm';
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        const file = new File([blob], `presentation-${Date.now()}.${ext}`, { type: mimeType });
        stream.getTracks().forEach(tr => tr.stop());
        setBioAudio(file);
        try {
          if (bioAudioPreviewUrl) URL.revokeObjectURL(bioAudioPreviewUrl);
          setBioAudioPreviewUrl(URL.createObjectURL(blob));
        } catch (_) {}
      };
      recordingStartRef.current = Date.now();
      mr.start();
      mediaRecorderRef.current = mr;
      setIsRecording(true);
      setRecordingDuration(0);

      // Tick + auto-stop at 60s max
      recordingTimerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - recordingStartRef.current) / 1000);
        setRecordingDuration(elapsed);
        if (elapsed >= 60) {
          stopRecordingBio();
        }
      }, 250);
    } catch (e) {
      toast.error('Accès au microphone refusé');
    }
  };

  const stopRecordingBio = () => {
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    mediaRecorderRef.current?.stop();
    setIsRecording(false);
    setRecordingDuration(0);
  };

  const clearBioAudio = () => {
    setBioAudio(null);
    if (bioAudioPreviewUrl) URL.revokeObjectURL(bioAudioPreviewUrl);
    setBioAudioPreviewUrl(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);

    try {
      // Upload profile picture
      if (profilePicture) {
        const formData = new FormData();
        formData.append('file', profilePicture);
        await axios.post('/api/users/me/profile-picture', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Photo de profil mise à jour');
      }

      // Upload bio audio
      if (bioAudio) {
        const formData = new FormData();
        formData.append('file', bioAudio);
        await axios.post('/api/users/me/bio-audio', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Audio de présentation ajouté');
      }

      // Build update object
      const updates = {};
      if (bio !== currentUser?.bio) updates.bio = bio;
      if (age !== currentUser?.age) updates.age = age ? parseInt(age) : null;
      if (dateOfBirth && dateOfBirth !== currentUser?.date_of_birth) updates.date_of_birth = dateOfBirth;
      if (isPro && siret !== currentUser?.siret) updates.siret = siret;
      if (phoneNumber !== (currentUser?.phone_number || '')) updates.phone_number = phoneNumber;

      // Username goes through its own endpoint (with uniqueness validation).
      // Only call if the user actually changed it AND it's not in an invalid/taken state.
      if (username && username !== currentUser?.username) {
        if (usernameStatus.available === false) {
          toast.error(usernameStatus.reason || "Nom d'utilisateur non disponible");
          setUploading(false);
          return;
        }
        try {
          await axios.patch('/api/users/me/username', { username });
          toast.success("Nom d'utilisateur mis à jour");
        } catch (e) {
          toast.error(e.response?.data?.detail || "Impossible de mettre à jour le nom d'utilisateur");
          setUploading(false);
          return;
        }
      }

      // Website URL update (available to all account types)
      if (websiteUrl.trim() !== (currentUser?.website_url || '').trim()) {
        try {
          await axios.post('/api/monetization/website', { website_url: websiteUrl.trim() });
        } catch (e) {
          console.error('Website url update failed', e);
        }
      }

      // Update profile data if changed
      if (Object.keys(updates).length > 0) {
        await axios.patch('/api/users/me', updates);
        toast.success('Profil mis à jour');
      }

      // Refresh AuthContext so the new profile picture propagates everywhere
      await refreshUser();
      onSuccess();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setUploading(false);
    }
  };

  const checkUsername = async (val) => {
    if (!val || val === currentUser?.username) {
      setUsernameStatus({ available: null, reason: '' });
      return;
    }
    try {
      const res = await axios.post('/api/users/check-username', { username: val });
      setUsernameStatus({ available: res.data.available, reason: res.data.reason || '' });
    } catch (e) {
      setUsernameStatus({ available: false, reason: e.response?.data?.detail || 'Erreur' });
    }
  };

  const handleUsernameChange = (raw) => {
    // Normalize while typing — username can only contain [a-z0-9_]
    const cleaned = raw.toLowerCase().replace(/[^a-z0-9_]/g, '').slice(0, 20);
    setUsername(cleaned);
    if (usernameCheckTimer.current) clearTimeout(usernameCheckTimer.current);
    usernameCheckTimer.current = setTimeout(() => checkUsername(cleaned), 350);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center overflow-y-auto pt-8 pb-4 px-4">
      <div
        className="bg-gray-900 border border-white/10 rounded-3xl max-w-lg w-full"
        style={{ marginBottom: keyboardHeight > 0 ? keyboardHeight + 20 : 0 }}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">{L.title}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Picture */}
            <div>
              <label className="block text-sm font-semibold mb-3">{L.photo}</label>
              <div className="flex items-center space-x-4">
                <div className="w-24 h-24 bg-gradient-to-br from-magenta-500 to-violet-600 rounded-full flex items-center justify-center overflow-hidden">
                  {profilePreview || currentUser?.profile_image_url ? (
                    <img
                      src={profilePreview || getMediaUrl(currentUser.profile_image_url)}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-3xl font-bold">
                      {currentUser?.name?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <label className="flex-1 cursor-pointer">
                  <div className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition-colors flex items-center justify-center space-x-2">
                    <ImageIcon className="w-5 h-5" />
                    <span className="text-sm font-medium">
                      {profilePicture ? profilePicture.name : L.choosePhoto}
                    </span>
                  </div>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleProfilePictureChange}
                    className="hidden"
                  />
                </label>
              </div>
              <p className="text-xs text-gray-400 mt-2">{L.photoHint}</p>
            </div>

            {/* Username — unique handle (@) */}
            <div>
              <label className="block text-sm font-semibold mb-2">{L.username}</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">@</span>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => handleUsernameChange(e.target.value)}
                  placeholder="destinestudio"
                  className={`w-full pl-8 pr-3 py-3 bg-white/5 border rounded-xl text-white focus:outline-none transition-colors ${
                    usernameStatus.available === false
                      ? 'border-red-500/60 focus:border-red-500'
                      : usernameStatus.available === true
                      ? 'border-green-500/60 focus:border-green-500'
                      : 'border-white/10 focus:border-magenta-500'
                  }`}
                  data-testid="username-input"
                  maxLength={20}
                  autoCapitalize="none"
                  autoCorrect="off"
                />
              </div>
              {usernameStatus.available === false && (
                <p className="text-xs text-red-400 mt-1.5" data-testid="username-error">
                  {usernameStatus.reason}
                </p>
              )}
              {usernameStatus.available === true && (
                <p className="text-xs text-green-400 mt-1.5" data-testid="username-ok">
                  {L.available}
                </p>
              )}
              {usernameStatus.available === null && (
                <p className="text-xs text-gray-500 mt-1.5">
                  {L.usernameRule}
                </p>
              )}
            </div>

            {/* Bio Text */}
            <div>
              <label className="block text-sm font-semibold mb-2">{L.bio}</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                onFocus={(e) => {
                  setTimeout(() => {
                    e.target.closest('.bg-gray-900')?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                  }, 300);
                }}
                rows={3}
                placeholder={L.bioPh}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-magenta-500 transition-colors resize-none"
                data-testid="bio-textarea"
              />
            </div>

            {/* Bio Audio */}
            <div>
              <label className="block text-sm font-semibold mb-3">{L.audioIntro}</label>

              {/* Mode toggle: Upload | Record */}
              <div className="flex gap-2 mb-3" data-testid="bio-audio-mode">
                <label className="flex-1 cursor-pointer">
                  <div className="px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl flex items-center justify-center gap-2 transition-colors text-sm">
                    <Upload className="w-4 h-4" />
                    <span>{bioAudio ? L.replace : L.upload}</span>
                  </div>
                  <input
                    type="file"
                    accept="audio/mpeg,audio/wav,audio/mp4,audio/ogg,audio/webm"
                    onChange={handleBioAudioChange}
                    className="hidden"
                    data-testid="bio-audio-file-input"
                  />
                </label>
                {isRecording ? (
                  <button
                    type="button"
                    onClick={stopRecordingBio}
                    className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-xl flex items-center justify-center gap-2 text-sm font-medium animate-pulse"
                    data-testid="bio-audio-stop-record"
                  >
                    <Square className="w-4 h-4" />
                    <span>{recordingDuration}s / 60</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={startRecordingBio}
                    className="flex-1 px-3 py-2 bg-violet-600 hover:bg-violet-700 rounded-xl flex items-center justify-center gap-2 text-sm font-medium"
                    data-testid="bio-audio-start-record"
                  >
                    <Mic className="w-4 h-4" />
                    <span>{L.record}</span>
                  </button>
                )}
              </div>

              {/* Audio status / preview */}
              <div className="px-4 py-3 bg-white/5 border border-white/10 rounded-xl flex items-center gap-3" data-testid="bio-audio-status">
                <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center shrink-0">
                  <Mic className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  {bioAudioPreviewUrl ? (
                    <>
                      <p className="text-sm font-medium truncate">{bioAudio?.name || L.newRecording}</p>
                      <audio src={bioAudioPreviewUrl} controls className="w-full mt-1 h-8" data-testid="bio-audio-preview" />
                    </>
                  ) : currentUser?.bio_audio_url ? (
                    <>
                      <p className="text-sm font-medium">{L.currentAudio}</p>
                      <audio src={currentUser.bio_audio_url} controls className="w-full mt-1 h-8" data-testid="bio-audio-current" />
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium">{L.noAudio}</p>
                      <p className="text-xs text-gray-400">{L.audioHint}</p>
                    </>
                  )}
                </div>
                {bioAudioPreviewUrl && (
                  <button
                    type="button"
                    onClick={clearBioAudio}
                    className="p-1.5 hover:bg-white/10 rounded-full text-gray-400 hover:text-red-400 shrink-0"
                    title="Annuler"
                    data-testid="bio-audio-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                <Phone className="w-4 h-4 text-green-400" />
                {L.phone}
              </label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                onFocus={(e) => {
                  setTimeout(() => {
                    e.target.closest('.bg-gray-900')?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                  }, 300);
                }}
                placeholder="+33 6 12 34 56 78"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-green-500 transition-colors"
                data-testid="phone-number-input"
              />
              <p className="text-xs text-gray-400 mt-1">Visible dans les discussions si activé dans Réglages &gt; Confidentialité</p>
            </div>

            {/* Website URL — available for all account types */}
            <div>
              <label className="block text-sm font-semibold mb-2 flex items-center gap-2">
                <ExternalLink className="w-4 h-4 text-cyan-400" />
                Lien externe
              </label>
              <input
                type="url"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="https://www.mon-site.com"
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500 transition-colors"
                data-testid="website-url-input"
              />
              <p className="text-xs text-gray-400 mt-1">{L.externalLinkHint}{isPro ? '.' : '.'}</p>
            </div>

            {/* Buttons */}
            <div className="flex items-center space-x-3 pt-4 pb-6">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-white/5 hover:bg-white/10 rounded-full font-semibold transition-colors"
              >
                {L.cancel}
              </button>
              <button
                type="submit"
                disabled={uploading || (!profilePicture && !bioAudio && bio === currentUser?.bio && phoneNumber === (currentUser?.phone_number || '') && username === (currentUser?.username || '') && websiteUrl.trim() === (currentUser?.website_url || '').trim())}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-magenta-500 to-violet-600 hover:from-magenta-600 hover:to-violet-700 rounded-full font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? L.saving : L.save}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProfileModal;
